/**
 * Live location-metrics dataset, loaded from a public CDN JSON at app start.
 *
 * This is the *only* source for per-area metrics (hover tooltip, selected-area
 * cards) — there is no bundled/generated fallback. Until this loads (or if the
 * fetch fails), callers get `undefined` and must render an explicit
 * "not available" state rather than invent a number — see `impactService.ts`.
 */
import type { AreaDataset } from '../types';

// TODO(infra): move behind config once there's a place to source it from
// (e.g. a runtime secret/config endpoint) rather than a literal here.
const LOCATION_METRICS_URL =
  'https://d1htq7yv9tanfs.cloudfront.net/philanthropy/location-metrics.json';

interface LocationMetrics {
  states: AreaDataset;
  counties: AreaDataset;
}

function isLocationMetrics(value: unknown): value is LocationMetrics {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.states === 'object' && typeof v.counties === 'object';
}

let cache: LocationMetrics | null = null;
let inFlight: Promise<void> | null = null;

async function fetchLocationMetrics(): Promise<void> {
  const res = await fetch(LOCATION_METRICS_URL);
  if (!res.ok) throw new Error(`location-metrics fetch failed: ${res.status}`);

  const json: unknown = await res.json();
  if (!isLocationMetrics(json)) {
    throw new Error('location-metrics.json has an unexpected shape.');
  }
  cache = json;
}

/**
 * Kick off the CDN load once (call from app start). Safe to call more than
 * once — concurrent/later calls share the same in-flight request. Never
 * throws — failures are logged and the app silently keeps using the bundled
 * fallback data.
 */
export function loadLocationMetrics(): Promise<void> {
  if (!inFlight) {
    inFlight = fetchLocationMetrics().catch((err: unknown) => {
      console.warn('[locationMetricsService] falling back to bundled data:', err);
    });
  }
  return inFlight;
}

/** Synchronous read of the live dataset, if it has loaded. */
export function getLiveAreaRecord(
  level: 'state' | 'county',
  fips: string,
): AreaDataset[string] | undefined {
  return cache?.[level === 'county' ? 'counties' : 'states']?.[fips];
}
